/**
 * SIEM hooks
 * For Media Devoted | Created By Ciro Autiero
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api';

export type SiemLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

export interface SiemEvent {
  id: string;
  ingestedAt: string;
  timestamp: string;
  level: SiemLevel;
  source: string;
  category: string;
  action: string | null;
  message: string;
  details: Record<string, unknown> | null;
  userId: string | null;
  userEmail: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  correlationId: string | null;
  requestId: string | null;
  httpMethod: string | null;
  geoipCountry: string | null;
  browser: string | null;
  os: string | null;
}

export interface SiemEventsResponse {
  events: SiemEvent[];
  pagination: { limit: number; offset: number; total: number };
}

export interface SiemFacetsResponse {
  levels: SiemLevel[];
  sources: string[];
  categories: string[];
  users: { id: string; email: string }[];
  httpMethods: string[];
  countries: string[];
}

export const siemKeys = {
  all: ['siem'] as const,
  facets: () => [...siemKeys.all, 'facets'] as const,
  uriPatterns: (httpMethod: string) =>
    [...siemKeys.all, 'uri-patterns', httpMethod] as const,
  list: (params: Record<string, unknown>) => [...siemKeys.all, 'events', params] as const,
  event: (id: string) => [...siemKeys.all, 'event', id] as const,
  stats: (params: Record<string, unknown>) => [...siemKeys.all, 'stats', params] as const,
  rules: () => [...siemKeys.all, 'detections', 'rules'] as const,
  alerts: (params: Record<string, unknown>) => [...siemKeys.all, 'detections', 'alerts', params] as const,
  alert: (id: string) => [...siemKeys.all, 'detections', 'alert', id] as const,
  alertEvents: (id: string) => [...siemKeys.all, 'detections', 'alert', id, 'events'] as const,
  dashboards: () => [...siemKeys.all, 'dashboards'] as const,
  dashboard: (id: string) => [...siemKeys.all, 'dashboards', id] as const,
};

export type SiemWidgetType = 'bar' | 'pie' | 'summary' | 'table' | 'timeSeries';

export interface SiemDashboardLayoutItem {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
  widgetType: SiemWidgetType;
  groupBy: SiemStatsGroupBy;
  title?: string;
  userId?: string;
  level?: string;
  limit?: number;
}

export interface SiemDashboard {
  id: string;
  name: string;
  layout: SiemDashboardLayoutItem[];
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

export function useSiemDashboards() {
  return useQuery({
    queryKey: siemKeys.dashboards(),
    queryFn: async () => {
      const res = await api.get<{ data: SiemDashboard[] }>('/siem/dashboards');
      return res.data.data;
    },
    staleTime: 30_000,
  });
}

export function useSiemDashboard(id: string | null) {
  return useQuery({
    queryKey: siemKeys.dashboard(id ?? ''),
    queryFn: async () => {
      const res = await api.get<{ data: SiemDashboard }>(`/siem/dashboards/${id}`);
      return res.data.data;
    },
    enabled: !!id,
    staleTime: 30_000,
  });
}

export function useCreateSiemDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (dto: { name: string; layout?: SiemDashboardLayoutItem[] }) => {
      const res = await api.post<{ data: SiemDashboard }>('/siem/dashboards', dto);
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siemKeys.dashboards() });
    },
  });
}

export function useUpdateSiemDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      dto,
    }: {
      id: string;
      dto: { name?: string; layout?: SiemDashboardLayoutItem[] };
    }) => {
      const res = await api.patch<{ data: SiemDashboard }>(`/siem/dashboards/${id}`, dto);
      return res.data.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: siemKeys.dashboards() });
      queryClient.invalidateQueries({ queryKey: siemKeys.dashboard(id) });
    },
  });
}

export function useDeleteSiemDashboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/siem/dashboards/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: siemKeys.dashboards() });
    },
  });
}

export interface SiemStatsResponse {
  series: { label: string; count: number }[];
  total: number;
}

export type SiemStatsGroupBy = 'level' | 'source' | 'user' | 'httpMethod' | 'country' | 'hour';

export function useSiemStats(input: {
  from?: string;
  to?: string;
  groupBy?: SiemStatsGroupBy;
  userId?: string;
  level?: string;
}) {
  const params = {
    from: input.from,
    to: input.to,
    groupBy: input.groupBy,
    userId: input.userId?.trim() ? input.userId : undefined,
    level: input.level?.trim() ? input.level : undefined,
  };
  return useQuery({
    queryKey: siemKeys.stats(params),
    queryFn: async () => {
      const res = await api.get<{ data: SiemStatsResponse }>('/siem/stats', { params });
      return res.data.data;
    },
    staleTime: 30_000,
  });
}

export function useSiemFacets() {
  return useQuery({
    queryKey: siemKeys.facets(),
    queryFn: async () => {
      const res = await api.get<{ data: SiemFacetsResponse }>('/siem/facets');
      return res.data.data;
    },
    staleTime: 60_000,
  });
}

const SIEM_HTTP_METHODS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']);

export function useSiemUriPatterns(httpMethod: string | undefined) {
  const hm = (httpMethod ?? '').trim().toUpperCase();
  return useQuery({
    queryKey: siemKeys.uriPatterns(hm),
    queryFn: async () => {
      const res = await api.get<{ data: string[] }>('/siem/facets/uri-patterns', {
        params: { httpMethod: hm },
      });
      return Array.isArray(res.data.data) ? res.data.data : [];
    },
    enabled: hm.length > 0 && SIEM_HTTP_METHODS.has(hm),
    staleTime: 60_000,
  });
}

export function useSiemEvents(input: {
  q?: string;
  level?: SiemLevel;
  source?: string;
  category?: string;
  userId?: string;
  userEmail?: string;
  httpMethod?: string;
  uriPattern?: string;
  geoipCountry?: string;
  from?: string;
  to?: string;
  limit: number;
  offset: number;
}) {
  const params = {
    q: input.q?.trim() ? input.q.trim() : undefined,
    level: input.level,
    source: input.source?.trim() ? input.source.trim() : undefined,
    category: input.category?.trim() ? input.category.trim() : undefined,
    userId: input.userId?.trim() ? input.userId.trim() : undefined,
    userEmail: input.userEmail?.trim() ? input.userEmail.trim() : undefined,
    httpMethod: input.httpMethod?.trim() ? input.httpMethod.trim() : undefined,
    uriPattern: input.uriPattern?.trim() ? input.uriPattern.trim() : undefined,
    geoipCountry: input.geoipCountry?.trim()
      ? input.geoipCountry.trim()
      : undefined,
    from: input.from,
    to: input.to,
    limit: input.limit,
    offset: input.offset,
  };

  return useQuery({
    queryKey: siemKeys.list(params),
    queryFn: async () => {
      const res = await api.get<{ data: SiemEventsResponse }>('/siem/events', { params });
      return res.data.data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useSiemEvent(id: string) {
  return useQuery({
    queryKey: siemKeys.event(id),
    queryFn: async () => {
      const res = await api.get<{ data: SiemEvent | null }>(`/siem/events/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

export interface SiemRule {
  id: string;
  name: string;
  description: string | null;
  enabled: boolean;
  severity: string;
  level: string | null;
  source: string | null;
  category: string | null;
  actionPrefix: string | null;
  windowSeconds: number;
  threshold: number;
  cooldownSeconds: number;
  lastTriggeredAt: string | null;
  lastRunAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface SiemAlert {
  id: string;
  ruleId: string;
  ruleName: string;
  severity: string;
  windowSeconds: number;
  threshold: number;
  matchCount: number;
  windowFrom: string;
  windowTo: string;
  sampleEventIds: string[] | null;
  details: Record<string, unknown> | null;
  createdAt: string;
}

export interface SiemAlertsResponse {
  alerts: SiemAlert[];
  pagination: { limit: number; offset: number; total: number };
}

export function useSiemRules() {
  return useQuery({
    queryKey: siemKeys.rules(),
    queryFn: async () => {
      const res = await api.get<{ data: SiemRule[] }>('/siem/detections/rules');
      return res.data.data;
    },
    staleTime: 30_000,
  });
}

export function useSiemAlerts(input: { severity?: string; limit: number; offset: number }) {
  const params = { severity: input.severity, limit: input.limit, offset: input.offset };
  return useQuery({
    queryKey: siemKeys.alerts(params),
    queryFn: async () => {
      const res = await api.get<{ data: SiemAlertsResponse }>('/siem/detections/alerts', { params });
      return res.data.data;
    },
    placeholderData: keepPreviousData,
  });
}

export function useSiemAlert(id: string) {
  return useQuery({
    queryKey: siemKeys.alert(id),
    queryFn: async () => {
      const res = await api.get<{ data: SiemAlert | null }>(`/siem/detections/alerts/${id}`);
      return res.data.data;
    },
    enabled: !!id,
  });
}

export function useSiemAlertEvents(alertId: string) {
  return useQuery({
    queryKey: siemKeys.alertEvents(alertId),
    queryFn: async () => {
      const res = await api.get<{ data: SiemEvent[] }>(`/siem/detections/alerts/${alertId}/events`);
      return res.data.data;
    },
    enabled: !!alertId,
  });
}

